import { Grid, Link, Stack, Typography, Container, Button, MenuItem } from "@mui/material";
import Image from "../../../components/minimal/image";
import { Checkout } from "./Checkout";
import Markdown from "../../../components/minimal/markdown";
import useResponsive from "../../../hooks/minimal/useResponsive";
import { extractHoursAndMinutes, fDate } from "../../../utils/minimal/formatTime";
import { CustomCard } from "./common";
import Iconify from "../../../components/minimal/iconify";
import { useTermsFormsForAccount } from "../../../hooks/useTermsForms.jsx";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useSettingsContext } from "../../../components/minimal/settings/SettingsContext";
import "./event-view-style-fix.css";
import { Helmet } from "react-helmet-async";
import { IFrameView } from "./IFrameView";
import { useSearchParams } from "react-router-dom";
import AddToCalendar from "./AddToCalendar";
import checkoutStore from "../../../stores/checkout.store";
import { useEffect, useState } from "react";
import { useEventContext } from "../../../context-state/EventContext";
import googleAnalytics from "../../../utils/googleAnalytics";
import { EventPageHeader } from "../../../layouts/main/EndUserHeader" //"../layouts/main/EndUserHeader";
import { useLocales } from "../../../locales";
import { MembersOnlyEvent } from "./MembersOnlyEvent";
import LanguageSelection from "../../../components/minimal/LanguageSelection";


const genDirectionLink = (event) => {
    const { location } = event
    // https://waze.com/ul?q=search_terms
    // https://www.google.com/maps/dir/?api=1&parameters
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`
}

const defaultBg = 'url(/assets/background/overlay_3.jpg)'

const buildEventDateString = (startDate, endDate, startHour, endHour) => {
    try {
        let dateString = fDate(startDate);
        let hourString = "";

        if (startHour && endHour) {
            hourString = `${extractHoursAndMinutes(startHour)}-${extractHoursAndMinutes(endHour)}`;
        } else if (startHour) {
            hourString = extractHoursAndMinutes(startHour);
        } else if (endHour) {
            hourString = extractHoursAndMinutes(endHour);
        }

        if (endDate && endDate !== startDate) {
            dateString = `${dateString}-${fDate(endDate)}`;
        }

        if (hourString) {
            dateString = `${dateString} ${hourString}`;
        }

        return dateString;
    } catch (error) {
        console.error("Failed to build event date string", error);
        return fDate(startDate);
    }
}

const FacebookPixelTags = ({ pixelId }) => {
    if (!pixelId) return null
    return (
        <Helmet>
            <script>
                {`
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${pixelId}');
                fbq('track', 'PageView');
                `}
            </script>
            <noscript>
                {`
                    <img height="1" width="1" style="display:none" 
                        src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>
                `}
            </noscript>
        </Helmet>
    )
}


export const EventView = ({ event, account, isPreview, overrideMessage }) => {
    const { translate } = useLocales();
    const isDesktop = useResponsive('up', 'md');
    const { presetsColor } = useSettingsContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const { eventPageContext } = useEventContext()
    const { tickets } = eventPageContext
    const qsBackgroundColor = searchParams.get('backgroundColor')
    let theme = useTheme()
    const { name, start_date, design, location, description, end_date, start_hour, end_hour, isOnlyForMembers } = event
    const { colors = {} } = design
    let { image, background } = design
    background = background ? background.replaceAll(" ", "%20") : ""
    image = image ? image.replaceAll(" ", "%20") : ""
    const eventDate = buildEventDateString(start_date, end_date, start_hour, end_hour)

    const { privacyPolicy, termsOfService } = useTermsFormsForAccount(account)
    const isLeanEvent = searchParams.get('isLeanEvent') || event.isLeanEvent
    const hrcQs = searchParams.get('hrc')


    theme = createTheme({
        palette: {
            primary: theme.palette.augmentColor({
                color: {
                    main: colors.buttons || theme.palette.primary.main,
                },
                name: 'primary',
            })
        }
    });

    useEffect(() => {
        if (!isPreview) {
            checkoutStore.init(event, tickets)
            googleAnalytics.init(event.google_analytics_id)
            googleAnalytics.pageView()
        }
    }, [])

    const noHrc = !hrcQs && !localStorage.getItem('hrcCode')
    if (event.isOnlyForMembers && noHrc) {
        return <>
            {event.showSellerHeader && <EventPageHeader />}
            <IFrameView>
                <MembersOnlyEvent account={account} />
            </IFrameView>
        </>
    }

    return (
        <>
            {event.showSellerHeader && <EventPageHeader />}
            <FacebookPixelTags pixelId={event.facebook_pixel} />
            <ThemeProvider theme={theme}>
                <IFrameView>
                    <Grid
                        justifyContent="center"
                        container
                        translate="no"
                        className="notranslate"
                        sx={{
                            p: isDesktop ? 8 : 0,
                            minHeight: "100vh",
                            backgroundColor: (qsBackgroundColor || colors.background || defaultBg),
                            backgroundImage: background ? `url(${background})` : (qsBackgroundColor || colors.background || defaultBg),
                            backgroundAttachment: "fixed",
                            backgroundPosition: 'center',
                            backgroundSize: 'cover'
                        }}>
                        <Stack spacing={2} sx={{ maxWidth: "1000px", minWidth: isDesktop ? "600px" : "0px" }}>
                            {
                                !isLeanEvent &&
                                <CustomCard sx={{ textAlign: "center", pb: 2 }}>
                                    <Image
                                        visibleByDefault
                                        alt="event-image"
                                        src={image}
                                        sx={{ maxWidth: 1000 }}
                                    />
                                    <Stack spacing={2}>
                                        <Typography variant="h3">
                                            {name}
                                        </Typography>

                                        <Stack alignItems="center">
                                            {
                                                location ?
                                                    <Stack spacing={1} direction="row" alignItems="center">
                                                        <Iconify icon="material-symbols:location-on-outline" />
                                                        <Typography variant="subtitle1">
                                                            {location} <Link href={genDirectionLink(event)} target="_blank" rel="noreferrer">{translate('directions')}</Link>
                                                        </Typography>
                                                    </Stack> : null
                                            }
                                            <Stack spacing={1} direction="row" alignItems="center">
                                                <Iconify icon="ic:sharp-access-time" />
                                                <Typography variant="subtitle1">
                                                    <span>{eventDate} <AddToCalendar event={event} /></span>
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </CustomCard>
                            }
                            {
                                !isLeanEvent &&
                                <CustomCard dir="ltr" sx={{ textAlign: "center", p: 2, maxWidth: "100vw" }}>
                                    <Markdown children={description} />
                                    <div dangerouslySetInnerHTML={{ __html: event.embedded_html }}></div>
                                </CustomCard>
                            }
                            {
                                overrideMessage ? <CustomCard sx={{ textAlign: "center", p: 2, maxWidth: "100vw" }}>
                                    <Typography variant="subtitle2" sx={{ color: "error.main" }}>
                                        {overrideMessage}
                                    </Typography>
                                </CustomCard> : null
                            }
                            {
                                isPreview ? null : <Checkout />
                            }

                            <Stack direction="row" sx={{ justifyContent: "center", gap: "20px", my: "16px !important" }}>
                                {termsOfService != 'hide' && <Link href={termsOfService} sx={{ color: colors.privacyPolicy, textDecorationColor: 'currentColor' }} target="_blank" rel="noreferrer">{translate('terms_of_service')}</Link>}
                                {privacyPolicy != 'hide' && <Link href={privacyPolicy} sx={{ color: colors.privacyPolicy, textDecorationColor: 'currentColor' }} target="_blank" rel="noreferrer">{translate('privacy_policy')}</Link>}
                                <LanguageSelection sx={{ color: colors.privacyPolicy, textDecorationColor: 'currentColor', }} />
                            </Stack>

                        </Stack>
                    </Grid>
                </IFrameView>
            </ThemeProvider>
        </>
    )
}
